import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import React, { useContext, useEffect, useState } from "react"
import { VibratoClientContext } from "./contexts"

const ApiKeysList: React.FC = () => {
  // const user = useContext(UserContext)
  // const upgradeModal = useContext(UpgradeModalContext)
  const [apiKeys, setApiKeys] = useState<{ name: string; uuid: string }[]>([])
  const vibratoClient = useContext(VibratoClientContext)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    document.title = "Vibrato - API Keys"
  }, [])

  const refreshKeys = () => {
    setRefresh(refresh + 1)
  }

  useEffect(() => {
    const fetchApiKeys = async () => {
      if (vibratoClient) {
        try {
          const keys = await vibratoClient.getApiKeys()
          setApiKeys(keys)
        } catch (error) {
          console.error("Failed to fetch API keys:", error)
        }
      }
    }

    fetchApiKeys()
  }, [vibratoClient, refresh])

  const deleteKey = async (uuid: string) => {
    await vibratoClient.deleteApiKey(uuid)
    setApiKeys(apiKeys.filter((key) => key.uuid !== uuid))
  }

  const onClick = () => {
    // if (user.plan == "freemium") {
    //   upgradeModal.setShowModal(true)
    //   return
    // }

    setShowCreateModal(true)
  }

  return (
    <div className="h-full p-8 flex flex-col gap-4">
      <CreateApiKeyModal
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          refreshKeys()
        }}
      />
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">API Keys</h1>
        <button
          className="bg-green-400 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg w-fit"
          onClick={() => onClick()}
        >
          Create API Key
        </button>
      </div>
      <div className="flex flex-col font-thin gap-2">
        <p>
          API keys are used to authenticate with the Vibrato API. You can create multiple keys to use in different
          environments or for different purposes. Be sure to keep your keys secure, as they provide access to your
          account.
        </p>
        <p>
          We have a beta API client for Python available{" "}
          <a href="https://github.com/vibratolabs/vibrato-python" className={"text-blue-500 hover:underline"}>
            here.{" "}
          </a>
          Documentation for the Vibrato API can be found{" "}
          <a href="https://docs.getvibrato.com" className={"text-blue-500 hover:underline"}>
            here.
          </a>
        </p>
      </div>
      <div className="flex flex-col max-h-full bg-white shadow-md rounded-lg">
        <div className="grow overflow-auto rounded-lg">
          <table className="max-h-full w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Key Name
                </th>
                <th scope="col" className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key) => (
                <tr key={key.uuid} className="border-b border-gray-200 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap dark:text-white">{key.name}</td>
                  <td className="flex gap-2 justify-end px-4 py-3 text-right">
                    <Button size="xs" pill onClick={() => deleteKey(key.uuid)} color="failure">
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {apiKeys.length === 0 && (
          <div className="flex justify-center items-center h-[80px] bg-gray-50 rounded-lg">
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">No API keys found.</span>
          </div>
        )}
      </div>
    </div>
  )
}

function CreateApiKeyModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const [name, setName] = useState<string | undefined>(undefined)
  const vibratoClient = useContext(VibratoClientContext)
  const [key, setKey] = useState(null)
  const [failureMessage, setFailureMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  const createKey = async (e: React.FormEvent) => {
    e.preventDefault()

    if (vibratoClient) {
      try {
        const key = await vibratoClient.createApiKey({ name })
        setKey(key.key)
        setSuccessMessage("API key created successfully. Please copy it now, as it will not be shown again.")
      } catch (error) {
        console.error("Failed to create API key:", error)
        setFailureMessage("There was an error creating the API key.")
      }
    }
  }

  const wrappedOnClose = () => {
    setKey(null)
    setName("")
    setFailureMessage("")
    setSuccessMessage("")
    onClose()
  }

  return (
    <Modal show={show} onClose={wrappedOnClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-purple-600">Create API Key</span>
      </ModalHeader>
      <ModalBody>
        <form id="create-key-form" onSubmit={createKey}>
          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-gray-700">Key Name</label>
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
              placeholder="Key Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              readOnly={key != null}
            />
          </div>
        </form>
        {key && (
          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-gray-700">Key</label>
            <div>
              <input
                type="text"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
                placeholder="Key"
                value={key}
                id="key-value"
                readOnly
              />
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {!key && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-key-form"
              className="mt-2 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-fit"
            >
              Create
            </button>
          </>
        )}
        <div>
          {successMessage && <div className="text-green-600">{successMessage}</div>}
          {failureMessage && <div className="text-red-500">{failureMessage}</div>}
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default ApiKeysList
